<template>
  <div class="comp-table-container">
    <el-table
      :data="tableData"
      :header-cell-style="getHeaderStyle"
      style="width: 100%"
      v-bind="$attrs"
    >
      <template #empty>
        <CompEmpty :text="emptyText" />
      </template>
      <template v-for="column in columns" :key="column.prop">
        <el-table-column
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :min-width="column.minWidth"
          :fixed="column.fixed"
          :sortable="column.sortable"
          :formatter="column.formatter"
        >
          
          <template #header="{ column: col }">
            <div :class="['header-cell', { 'ellipsis': column.ellipsis }]" :style="getHeaderCellStyle(column)">
              {{ col.label }}
            </div>
          </template>
          
          <template #default="scope" v-if="column.slotName">
            <slot :name="column.slotName" :row="scope.row" :scope="scope" :$index="scope.$index"></slot>
          </template>
        </el-table-column>
      </template>
      
      <!-- 操作列插槽 -->
      <el-table-column v-if="$slots.actions" :label="actionLabel" :width="actionColumnWidth" fixed="right">
        <template #default="scope">
          <slot name="actions" :row="scope.row" :scope="scope" :$index="scope.$index"></slot>
        </template>
      </el-table-column>
    </el-table>
    
    <div v-if="!customPagination && showPagination" class="pagination-container">
      <div class="pagination-left"></div>
      <div class="pagination-right">
        <el-pagination
          background
          v-model:current-page="currentPage"
          :page-size="currentPageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
    <!-- 自定义左右分页 -->
    <div v-if="customPagination && showPagination" class="pagination-container">
      <div class="pagination-left">
        <el-pagination
          v-model:current-page="currentPage"
          background
          :page-size="currentPageSize"
          layout="total, sizes"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
      <div class="pagination-right">
        <el-pagination
          v-model:current-page="currentPage"
          background
          :page-size="currentPageSize"
          :total="total"
          layout="prev, pager, next, jumper"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import CompEmpty from '@/components/comp-empty/index.vue';
import { Lang } from '@/lang/lang.js'

const props = defineProps({
  // 表格数据
  data: {
    type: Array,
    default: () => []
  },
  // 列配置
  columns: {
    type: Array,
    default: () => []
  },
  // 总条数
  total: {
    type: Number,
    default: 0
  },
  // 当前页码
  page: {
    type: Number,
    default: 1
  },
  // 每页条数
  pageSize: {
    type: Number,
    default: 10
  },
  // 表头背景色
  headerBgColor: {
    type: String,
    default: '#f5f7fa'
  },
  // 表头文字颜色
  headerTextColor: {
    type: String,
    default: '#606266'
  },
  // 表头最大行数
  headerMaxLines: {
    type: Number,
    default: 1
  },
  // 操作列宽度
  actionColumnWidth: {
    type: Number,
    default: 120
  },
  // 操作列标题
  actionLabel: {
    type: String,
    default: Lang.Title.modelAction,
  },
  emptyText: {
    type: String,
    default: Lang.Text.basicTxNoData
  },
  showPagination: {
    type: Boolean,
    default: true,
  },
  customPagination: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:page', 'update:pageSize', 'page-change', 'size-change'])

const currentPage = ref(props.page)
const currentPageSize = ref(props.pageSize)

const tableData = ref(props.data)

// 计算总页数
const totalPage = computed(() => {
  return Math.ceil(props.total / props.pageSize)
})

// 处理表头样式
const getHeaderStyle = () => {
  return {
    backgroundColor: props.headerBgColor,
    color: props.headerTextColor
  }
}

// 处理表头单元格样式
const getHeaderCellStyle = (column) => {
  const style = {}
  if (column.headerBgColor) {
    style.backgroundColor = column.headerBgColor
  }
  if (column.headerTextColor) {
    style.color = column.headerTextColor
  }
  if (props.headerMaxLines > 1) {
    style.whiteSpace = 'normal'
    style.wordBreak = 'break-word'
    style.lineHeight = '1.5'
    style.display = '-webkit-box'
    style.webkitLineClamp = props.headerMaxLines
    style.webkitBoxOrient = 'vertical'
    style.overflow = 'hidden'
    style.fontWeight = 'normal' // 历史数据表头
  }
  return style
}

// 页码变化事件
const handleCurrentChange = (val) => {
  currentPage.value = val
  emit('update:page', val)
  emit('page-change', val)
}
// 页数变化
const handleSizeChange = (size) => {
  currentPageSize.value = size
  emit('update:pageSize', size)
  emit('size-change', size)
}

// 监听props变化
watch(() => props.page, (newVal) => {
  if (newVal !== currentPage.value) {
    currentPage.value = newVal
  }
})

watch(() => props.pageSize, (newVal) => {
  if (newVal !== currentPageSize.value) {
    currentPageSize.value = newVal
  }
})

watch(() => props.data, (newVal) => {
  tableData.value = newVal
})
</script>

<style scoped>
.comp-table-container {
  /* border: 1px solid #E3E8F2; */
  border-radius: 8px;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px;
  /* padding: 0 12px; */
  box-sizing: border-box;
}

.pagination-left {
  font-size: 14px;
  color: #606266;
}

.header-cell.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
:deep(.el-table__header-wrapper .el-table__cell) {
  position: relative;
}

:deep(.el-table__header-wrapper .el-table__cell .el-icon) {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 4px;
}

/* 如果表头有多行文本，需要额外调整 */
:deep(.el-table__header-wrapper .el-table__cell .cell) {
  display: inline-flex;
  align-items: center;
}
:deep(.el-pager li.is-active) {
  background-color: #268BFC;
  color: #ffffff;
}
:deep(.el-table__empty-text) {
  line-height: 24px;
  font-size: 14px;
  color: #616D7C;
}
</style>